import {
  buildGuide,
  paragraphs,
  bullets,
  steps,
  callout,
  workflow,
  faq,
  table,
  fieldTable,
  permissionTable,
  heading,
} from '../helpers';

const cfgPerm = permissionTable([
  ['manage_configuration', 'Required to edit Configuration master data'],
]);

export const configurationGuides = [
  buildGuide({
    id: 'configuration-overview',
    title: 'Configuration Overview',
    moduleId: 'configuration',
    summary:
      'Property master data: identity, locale, rooms, rates, taxes, media, policies, users, and communications.',
    keywords: ['configuration', 'settings', 'setup', 'master data', 'hotel config'],
    pathHint: 'Hotel → Configuration',
    related: [
      'property-setup',
      'basic-details',
      'room-types',
      'rooms',
      'rate-plans',
      'finance-setup',
    ],
    overview: [
      ...paragraphs(
        'Configuration is where managers define how the hotel works. Front office, distribution, POS, and billing all depend on these masters — if rooms, rates, or taxes are wrong, every booking and invoice hurts.',
        'Use Configuration when opening a new property, adding a wing, launching a meal plan, changing GST details, or updating guest-facing policies. Day-to-day FO work (walk-ins, check-in) should not require Configuration rights.',
        'Change masters carefully on a live hotel: renaming room types breaks OTA maps; editing tax mid-cycle confuses GST reports; deleting floors with rooms attached creates orphan records.',
        'Follow Property Setup order for new hotels: locale → basic identity → floors → room types → rooms → rate plans → Manage Rate → finance/invoice → amenities/images/terms → communications → users.'
      ),
      table(
        ['Screen', 'Purpose'],
        [
          ['Config', 'Timezone, country, currency, date format + Communications'],
          ['Basic Details', 'Property name, contacts, address, social, check-in/out times'],
          ['Rate Plan', 'Pricing products / meal plans (RO, CP, MAP…)'],
          ['Room types', 'Sellable categories, capacity, nightly/hourly mode'],
          ['Floors', 'Physical floor masters for rooms & HK'],
          ['Rooms', 'Door numbers linked to type + floor'],
          ['Images', 'File manager / property media'],
          ['Amenities', 'Facilities by category for merchandising'],
          ['Terms & Policy', 'Cancellation, check-in/out, pets, smoking, etc.'],
          ['Finance Setup', 'Tax rules, GSTIN, PAN'],
          ['Invoice Setup', 'Invoice templates, logo text, bank/HSN fields'],
          ['Users', 'Hotel-scoped staff accounts (with manage_users)'],
        ]
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets([
        'Know legal property name, GSTIN, and address',
        'Have a room inventory list (floor → number → type)',
        'Agree meal plans and inclusions with F&B / ownership',
        'Coordinate OTA remapping if renaming live room types',
      ]),
      callout(
        'important',
        'Least privilege',
        'Give FO booking rights without configuration rights. Master-data mistakes are expensive.'
      ),
    ],
    steps: [
      heading('Safe change process'),
      steps([
        {
          text: 'Open Hotel → Configuration and confirm the correct property in the top bar.',
        },
        {
          text: 'Follow Property Setup Guide order for new properties; for live hotels, change one master area at a time.',
        },
        {
          text: 'Save each screen before moving on. Watch for required-field validation.',
        },
        {
          text: 'If room types or rate plans changed, update Distribution → Channel Mapping and Manage Rate.',
        },
        {
          text: 'Create a test booking and sample invoice to verify taxes, rates, and identity.',
        },
        {
          text: 'Brief FO on any guest-facing change (check-in time, cancellation, new category names).',
        },
      ]),
    ],
    screens: [
      bullets(
        [
          'Configuration hub with section cards / NotchTabs',
          'Progress indicators on cards (completion cues)',
          'Embedded editors for each master (Basic Details, Rooms, Finance…)',
        ],
        'Configuration hub'
      ),
      fieldTable([
        ['Config', '—', 'Locale + Communications toggles'],
        ['Basic Details', '—', 'Identity, contacts, address, CI/CO times'],
        ['Room types / Floors / Rooms', '—', 'Physical sellable structure'],
        ['Rate Plan', '—', 'Sellable pricing products'],
        ['Finance / Invoice Setup', '—', 'Tax engine + invoice branding'],
      ]),
    ],
    workflow: [
      workflow('Change room type name on a live hotel', [
        'Edit Room Types',
        'Update Manage Rate if needed',
        'Fix Distribution Channel Mapping',
        'Inform FO + revenue',
        'Test OTA + walk-in',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Masters are referenced by bookings, invoices, channels, and POS. Deleting or renaming carelessly breaks history display, maps, and reports — prefer deactivate/disable over hard delete when the UI allows.',
        'Locale (timezone/currency) affects night boundaries, report dates, and money display. Wrong timezone creates off-by-one night errors on arrivals and night audit.',
        'Tax rules in Finance Setup apply at billing time. Invoice Setup controls presentation (logo, HSN/SAC, bank details) but does not replace tax logic.',
        'Organisation-level users/roles live on Landing Settings; property masters live here. Hotel Users manage staff for the selected property when permitted.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Quiet hours',
        'Change masters in quiet hours and keep a short config change log (who/what/when).'
      ),
      callout(
        'warning',
        'Tax mid-month',
        'Do not edit tax percentages mid-month without CA advice — GST reports will disagree with filed returns.'
      ),
      callout(
        'note',
        'Test after save',
        'A saved screen is not verified until a test booking/invoice proves the change.'
      ),
    ],
    bestPractices: [
      bullets([
        'Change masters in quiet hours with FO informed.',
        'Keep a config change log for audits.',
        'Freeze room type names before OTA go-live.',
        'Align legal name / GSTIN across Basic Details, Finance Setup, and Invoice Setup.',
        'Use Property Setup checklist for every new hotel in the group.',
        'Separate configuration admins from cashiers.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Editing tax mid-month without CA advice.',
        'Renaming room types without remapping OTAs.',
        'Creating rate plans without Manage Rate amounts.',
        'Giving every supervisor full configuration rights.',
        'Working in the wrong hotel property after a switch.',
        'Skipping Communications test after enabling WhatsApp.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Save failed',
            'Required fields or permissions',
            'Complete highlighted fields; confirm manage_configuration',
          ],
          [
            'FO still sees old data',
            'Browser cache / session',
            'Refresh; re-login; confirm correct hotel',
          ],
          [
            'Module section missing',
            'Package or permission',
            'Ask admin to enable Configuration for the role',
          ],
          [
            'Booking cannot find rates',
            'Rate plan or Manage Rate incomplete',
            'Create plan + set daily rates for the date range',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Org settings vs hotel config?',
          a: 'Org users/roles and subscription live on Landing → Settings. Property masters (rooms, rates, tax) live under Hotel → Configuration.',
        },
        {
          q: 'Who should access Configuration?',
          a: 'Property managers, revenue, and designated config admins — not every receptionist.',
        },
        {
          q: 'Do I need Configuration to take a walk-in?',
          a: 'No, if rooms and rates already exist. You need Configuration only to create or change those masters.',
        },
        {
          q: 'Order of setup for a new hotel?',
          a: 'Follow Property Setup: locale → identity → floors/types/rooms → rate plans → Manage Rate → finance/invoice → media/policies → communications → users.',
        },
        {
          q: 'Will changing config rewrite old bookings?',
          a: 'Historical bookings keep past values where stored on the booking/folio. Future behaviour follows new masters — still avoid reckless renames.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'basic-details',
    title: 'Basic Details',
    moduleId: 'configuration',
    summary:
      'Set hotel name, description, contacts, address, check-in/out times, and social links.',
    keywords: [
      'basic details',
      'hotel name',
      'address',
      'contact',
      'check-in time',
      'whatsapp',
    ],
    pathHint: 'Configuration → Basic Details',
    related: ['hotel-config', 'invoice-setup', 'nexastay-setup', 'communications'],
    overview: [
      ...paragraphs(
        'Basic Details define how the property is identified to staff, guests, and documents: property name, description, contact channels, address/geo, and standard check-in / check-out times.',
        'Accurate phones and WhatsApp numbers matter in India and similar markets — confirmations, payment links, and guest replies fail when numbers are wrong.',
        'Check-in and check-out times here inform guest expectations and ops planning; keep them aligned with Terms & Policy and what OTAs publish.',
        'After a rebrand, update Basic Details together with Invoice Setup logo/headers and NexaStay theme so guests never see mixed identities.'
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets([
        'Legal / trading name agreed with accounts',
        'Primary WhatsApp, email, and mobile that front desk monitors',
        'Full address with pincode and map coordinates if used',
        'Published CI/CO times for the brand',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Configuration → Basic Details.',
        },
        {
          text: 'Set Property name (display/legal identity as used operationally) and Property Description for merchandising.',
        },
        {
          text: 'Set Check-in and Check-out times to match house rules and OTA listings.',
        },
        {
          text: 'Enter Property WhatsApp Number, Email Address, Mobile Number, and Landline as applicable. Keep WhatsApp reachable if Communications uses it.',
        },
        {
          text: 'Add social profile links (Facebook, LinkedIn, Twitter/X, Instagram) and review/TripAdvisor/Google links if you publish them.',
        },
        {
          text: 'Update hotel address: City, Pincode, and Latitude / Longitude when map assist is used.',
        },
        {
          text: 'Save. Verify invoice headers and NexaStay/contact surfaces show the new identity.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Property name', 'Yes', 'Primary hotel identity in app and documents'],
        ['Property Description', 'No', 'Marketing / about text'],
        ['Check-in / Check-out', 'Recommended', 'Standard arrival and departure times'],
        ['Property Whatsapp Number', 'Recommended', 'Guest messaging channel'],
        ['Property Email Address', 'Recommended', 'Guest and system email contact'],
        ['Property Mobile / Landline', 'Recommended', 'Voice contact numbers'],
        ['Social / review links', 'No', 'Facebook, Instagram, TripAdvisor, Google, etc.'],
        ['City / Pincode', 'Yes (typical)', 'Address components'],
        ['Latitude / Longitude', 'No', 'Map placement / geo assist'],
      ]),
    ],
    workflow: [
      workflow('Rebrand', [
        'Update Basic Details',
        'Update Invoice Setup logo/headers',
        'Update NexaStay theme/slug if needed',
        'Inform channels / marketing',
        'Spot-check guest emails',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Identity fields flow into guest-facing and fiscal documents where configured. Invoice Setup may still hold separate header/tagline/signatory text — keep them consistent.',
        'WhatsApp and email values support Communications and staff outreach. Wrong numbers look like “system failure” to guests.',
        'Check-in/out times are operational standards; early CI / late CO exceptions are still handled in booking notes and charges, not by silently changing these defaults every day.'
      ),
    ],
    tips: [
      callout(
        'note',
        'Legal name vs brand name',
        'GST invoices may need the legal entity name — align with Finance Setup GSTIN and Invoice Setup headers.'
      ),
      callout(
        'tip',
        '24×7 reachable number',
        'If you advertise a phone, ensure night audit can answer or divert it.'
      ),
      callout(
        'warning',
        'Typo in email',
        'A single-character email typo breaks guest replies and password resets routed to property contacts.'
      ),
    ],
    bestPractices: [
      bullets([
        'Keep phone reachable 24×7 if advertised.',
        'Match CI/CO times to OTA and Terms & Policy text.',
        'Update Google/TripAdvisor links after ownership changes.',
        'Use map lat/long so NexaStay/maps point to the real gate.',
        'Review Basic Details quarterly for stale social URLs.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Typos in email used for guest replies.',
        'Updating name but leaving old logo on invoices.',
        'Publishing a WhatsApp number nobody monitors.',
        'CI/CO times that contradict OTA listings.',
        'Leaving old city/pincode after relocation.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Old name on invoice',
            'Invoice Setup not updated',
            'Update Invoice Setup headers/logo; regenerate sample PDF',
          ],
          [
            'WhatsApp not delivering',
            'Wrong number or Communications off',
            'Fix WhatsApp on Basic Details; enable toggles in Config → Communications',
          ],
          [
            'Map pin wrong',
            'Bad lat/long',
            'Correct Latitude/Longitude via address assist; re-save',
          ],
          [
            'Cannot save',
            'Required validation',
            'Complete property name and required address/contact fields',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Multiple brands?',
          a: 'Each hotel property has its own Basic Details. Switch hotel before editing.',
        },
        {
          q: 'Does description appear on NexaStay?',
          a: 'Property description and related content feed guest-facing surfaces where wired — keep it accurate and current.',
        },
        {
          q: 'Can FO edit Basic Details?',
          a: 'Only if they have manage_configuration. Prefer a manager account for identity changes.',
        },
        {
          q: 'Landline required?',
          a: 'No — enter what you actually publish. Mobile + WhatsApp are often enough.',
        },
        {
          q: 'Check-in time vs early arrival?',
          a: 'Defaults set expectations; early arrivals are operational exceptions with notes/charges as per SOP.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'hotel-config',
    title: 'Hotel Config (Locale & Settings)',
    moduleId: 'configuration',
    summary:
      'Timezone, country, currency, date format, and Communications toggles for the property.',
    keywords: [
      'timezone',
      'currency',
      'country',
      'date format',
      'communications',
      'locale',
    ],
    pathHint: 'Configuration → Config',
    related: ['basic-details', 'communications', 'finance-setup'],
    overview: [
      ...paragraphs(
        'Config controls locale behaviour so nights, reports, and messages align with how your property operates: timezone, country, currency, and date format.',
        'Wrong timezone is a silent killer — arrivals, night audit, and “today” on the Dashboard can shift by a day relative to the front desk clock.',
        'Currency affects how money fields display. Keep it aligned with bank accounts and Finance Setup tax currency practice.',
        'Communications settings are embedded with Config: master Email/WhatsApp channels and per-event toggles for booking, payments, reviews, offers, and housekeeping alerts.'
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets([
        'Confirm property timezone with GM (airport/city local time)',
        'Confirm currency used on tariffs and bank settlement',
        'Know which message channels are contractually available',
      ]),
      callout(
        'warning',
        'Do not change timezone casually',
        'Coordinate with accounting before timezone changes on a live hotel — historical “day” boundaries can confuse reports.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open Configuration → Config.',
        },
        {
          text: 'Set Timezone (searchable list), Country, Currency, and Date format.',
        },
        {
          text: 'Save locale fields. Create a test booking spanning midnight to confirm night counting looks right.',
        },
        {
          text: 'Open Communications within Config. Enable master Email and/or WhatsApp only if integrations are healthy.',
        },
        {
          text: 'Toggle event messages you want live (booking created/cancelled, payment, review, offers, HK alerts, report cadences).',
        },
        {
          text: 'Send a test confirmation on a test booking; train FO when to resend manually.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Timezone', 'Yes', 'Local property time for nights and “today”'],
        ['Country', 'Yes', 'Locale / address defaults context'],
        ['Currency', 'Yes', 'Money display currency'],
        ['Date format', 'Yes', 'How dates appear in UI'],
        ['Communications master toggles', 'Depends', 'Email / WhatsApp channel enablement'],
        ['Event toggles', 'Depends', 'Booking, payment, review, offers, HK, reports'],
      ]),
      bullets(
        [
          'Locale card with searchable selects',
          'CommunicationSettings embedded panel',
        ],
        'What you see'
      ),
    ],
    workflow: [
      workflow('New property locale', [
        'Set timezone/country/currency/date format',
        'Save',
        'Test booking dates across midnight',
        'Configure Communications',
        'Test guest message',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Timezone drives check-in/out date interpretation and daily reports. Currency is display/settlement context for money fields — keep Finance Setup rules consistent.',
        'Communications sends depend on guest contact fields and provider connectivity. Master channel off means event toggles cannot deliver on that channel.',
        'Date format is presentation; storage remains consistent — still train staff so handwritten logs match the on-screen format.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Match the wall clock',
        'If the lobby clock is IST, timezone must be Asia/Kolkata (or equivalent) — not a head-office zone abroad.'
      ),
      callout(
        'warning',
        'Currency clarity',
        'INR UI with verbal USD quotes confuses guests — document foreign-currency SOP separately.'
      ),
      callout(
        'note',
        'Comms quiet hours',
        'Enable only message types you will honour. Offer spam damages brand faster than silence.'
      ),
    ],
    bestPractices: [
      bullets([
        'Match currency to bank accounts and filed GST currency practice.',
        'Enable only channels you monitor (Email/WhatsApp).',
        'Re-test messages after provider credential rotation.',
        'Document the property timezone in the opening SOP.',
        'Change locale settings with accounting present for live hotels.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Currency INR UI but quoting USD verbally without clarity.',
        'Head-office timezone on a resort in another state/country.',
        'Enabling WhatsApp without a monitored number on Basic Details.',
        'Turning on all offer messages on day one.',
        'Changing date format mid-training week without telling FO.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'WhatsApp not sending',
            'Communications toggles or integration health',
            'Enable master + event toggles; verify WhatsApp number; check provider',
          ],
          [
            'Wrong “today” on Dashboard',
            'Timezone mismatch',
            'Correct timezone; re-login; verify hotel date helper',
          ],
          [
            'Amounts look wrong symbol',
            'Currency setting',
            'Set correct currency; refresh rate screens',
          ],
          [
            'Cannot find timezone',
            'Search spelling',
            'Search city/region keywords in the timezone picker',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Languages?',
          a: 'Staff UI follows product language support; guest messages depend on templates/comms setup.',
        },
        {
          q: 'Is Communications a separate menu?',
          a: 'It is managed inside Configuration → Config (also reachable via communications routes that open Config).',
        },
        {
          q: 'Can I use multiple currencies?',
          a: 'Property Config sets the hotel currency context. Follow your fiscal SOP for foreign charges.',
        },
        {
          q: 'What breaks if timezone is wrong?',
          a: 'Night counts, arrivals lists, reports, and automated “today” jobs can shift by a day.',
        },
        {
          q: 'Who edits Config?',
          a: 'Configuration admins / managers with manage_configuration — not cashiers.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'communications',
    title: 'Communications (Email / WhatsApp / SMS)',
    moduleId: 'configuration',
    summary:
      'Toggle booking, payment, review, offer, housekeeping, and report messages across Email and WhatsApp.',
    keywords: [
      'whatsapp',
      'email',
      'sms',
      'notifications',
      'review message',
      'booking confirmation',
    ],
    pathHint: 'Configuration → Config → Communications',
    related: ['hotel-config', 'ai-retention', 'booking-detail', 'basic-details'],
    overview: [
      ...paragraphs(
        'Communications settings decide which automatic or staff-triggered messages can send — booking created/cancelled, payment received, payment links, invoice vouchers, review asks, customer offers, housekeeping maintenance / lost & found alerts, and report cadences.',
        'Master Email and WhatsApp switches gate the channel. Event-level toggles then choose what actually fires. Manual-trigger events (review, payment link, offers) still need FO discipline.',
        'Delivery requires accurate guest mobile/email on the booking and healthy provider integrations. Failures should be retried or switched to a manual call/WhatsApp from the desk.',
        'In many Indian properties WhatsApp is read faster than email — keep Basic Details WhatsApp numbers accurate and monitored.'
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets([
        'Channel credentials/integrations healthy',
        'Property WhatsApp/email correct on Basic Details',
        'FO trained on when to send manual review/payment-link messages',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Configuration → Config and scroll to Communications.',
        },
        {
          text: 'Enable master Email and/or WhatsApp only for channels you operate.',
        },
        {
          text: 'For each event (booking created, booking cancelled, payment received, payment link, invoice voucher, HK maintenance, HK lost & found, review guest, customer offer), enable Email/WhatsApp as needed.',
        },
        {
          text: 'Configure report cadences (daily / weekly / monthly) if hotel report messages are used.',
        },
        {
          text: 'Create a test booking and confirm booking_created delivery to a staff test number/email.',
        },
        {
          text: 'Train FO: which events are auto, which are manual, and quiet-hour expectations.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Master Email / WhatsApp', 'Yes for delivery', 'Channel-level enablement'],
        ['booking_created / cancelled', 'Optional', 'Auto booking confirmations and cancel notices'],
        ['payment_received / payment_link', 'Optional', 'Payment confirmation and pay-link sends'],
        ['invoice_voucher', 'Optional', 'Invoice/voucher messaging'],
        ['review_guest', 'Optional', 'Manual review ask with link'],
        ['customer_offer', 'Optional', 'Promotional offers — use sparingly'],
        ['HK maintenance / lost & found', 'Optional', 'Staff alerts for HK workflows'],
        ['Report cadences', 'Optional', 'Daily / weekly / monthly report messages'],
      ]),
    ],
    workflow: [
      workflow('Enable review ask', [
        'Turn on review_guest for WhatsApp/Email',
        'Checkout guest',
        'FO sends review message',
        'Confirm link received',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Auto events fire from system triggers when enabled; manual events require staff action. Master channel off blocks all events on that channel.',
        'Sends use guest contact fields on the booking/customer record. Missing mobile cannot WhatsApp; missing email cannot email.',
        'Housekeeping events alert hotel staff (email/WhatsApp to hotel contacts as configured) when maintenance or lost & found is recorded.',
        'Offers and review asks affect brand reputation — enable only with an honour process at the desk.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'WhatsApp first in India',
        'Many guests read WhatsApp faster than email — keep numbers accurate and confirmed at booking.'
      ),
      callout(
        'warning',
        'Offer spam',
        'Do not enable customer_offer daily. Guests mute brands that spam.'
      ),
      callout(
        'note',
        'Quiet hours',
        'Agree FO quiet hours for manual sends (reviews/offers), especially late check-outs.'
      ),
    ],
    bestPractices: [
      bullets([
        'Respect quiet hours for manual messages.',
        'Test one event at a time when going live.',
        'Keep templates aligned with Terms & Policy.',
        'Document which role may send payment links.',
        'Disable unused events instead of leaving noise on.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Enabling offers without FO honour process.',
        'Master WhatsApp on but property number wrong.',
        'Expecting SMS when only Email/WhatsApp are wired.',
        'Turning on everything during go-live week.',
        'No test send before first VIP arrival.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Not received',
            'Wrong number/email, toggle off, provider down',
            'Check contact fields; toggles; provider logs; resend manually',
          ],
          [
            'Only some events work',
            'Per-event toggles',
            'Enable the specific event on the intended channel',
          ],
          [
            'Staff HK alerts missing',
            'HK event hotel toggles off',
            'Enable housekeeping_maintenance / lost_found hotel Email/WhatsApp',
          ],
          [
            'Guest complaint about spam',
            'Offers too frequent',
            'Disable customer_offer; apologise; tighten SOP',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'SMS?',
          a: 'Where SMS is wired in your deployment, treat it like other channels — otherwise rely on WhatsApp/Email as configured in Communications.',
        },
        {
          q: 'Are review messages automatic?',
          a: 'review_guest is a manual-style trigger in the product model — FO sends at the right moment (often post check-out).',
        },
        {
          q: 'Do cancelled bookings notify guests?',
          a: 'When booking_cancelled is enabled on a live channel and contacts exist, yes.',
        },
        {
          q: 'Can I use Email only?',
          a: 'Yes — enable Email master and events; leave WhatsApp off.',
        },
        {
          q: 'Where do report cadences go?',
          a: 'Configured under Communications report settings (daily/weekly/monthly) when used by your hotel.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'floors',
    title: 'Floors',
    moduleId: 'configuration',
    summary: 'Create hotel floors used to organise rooms and housekeeping boards.',
    keywords: ['floors', 'floor master', 'wing', 'floor number'],
    pathHint: 'Configuration → Floors',
    related: ['rooms', 'housekeeping-room-status'],
    overview: [
      ...paragraphs(
        'Floors group rooms for navigation, assignment, and housekeeping boards. Every physical room should sit on a floor that matches signage.',
        'Create floors before bulk room entry. Naming should match what staff say aloud (“Ground”, “1”, “2” or “Pool Wing L1”) so HK and FO stay aligned.',
        'Disabled floors should not receive new rooms. Avoid deleting floors that still have rooms attached.',
        'When opening a new wing, create floors first, then rooms, then enable POS room service rooms if needed.'
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets(['Floor plan / signage list', 'Agree naming with HK supervisor']),
    ],
    steps: [
      steps([
        {
          text: 'Open Configuration → Floors.',
        },
        {
          text: 'Add Floor Number (required) and optional Floor Name / Description.',
        },
        {
          text: 'Leave Enabled/Active on for floors in use. Save.',
        },
        {
          text: 'Repeat for all floors/wings before creating rooms.',
        },
        {
          text: 'Assign rooms to these floors on the Rooms screen.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Floor Number', 'Yes', 'Primary grouping key / sort'],
        ['Floor Name', 'No', 'Friendly label (Ground, Terrace…)'],
        ['Description', 'No', 'Ops notes'],
        ['Enabled / Active', 'Yes', 'Whether floor is available for assignment'],
      ]),
    ],
    workflow: [
      workflow('Add wing', [
        'Create floors',
        'Create rooms on those floors',
        'HK board check',
        'Enable POS rooms if needed',
        'Sell',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Rooms reference floors. Deleting floors with rooms attached should be avoided — reassign rooms first.',
        'Housekeeping boards and room lists often group by floor; inconsistent names slow room assignment during peak arrivals.',
        'Disabled floors hide from normal assignment flows while preserving history.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Naming',
        'Use “1”, “2” or “Ground” consistently with signage and elevator panels.'
      ),
      callout(
        'note',
        'Wings',
        'If you have disconnected blocks, encode the wing in Floor Name (e.g. “Garden-1”).'
      ),
      callout(
        'warning',
        'Duplicates',
        'Duplicate floor numbers confuse room forms — keep numbers unique per property.'
      ),
    ],
    bestPractices: [
      bullets([
        'Create all floors before bulk room entry.',
        'Match HK board labels exactly.',
        'Disable unused floors instead of deleting when possible.',
        'Document floor → fire exit notes in description if helpful.',
        'Review floors when renovations renumber wings.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Duplicate floor numbers.',
        'Creating rooms before floors exist.',
        'Names that do not match elevator signage.',
        'Deleting a floor that still has rooms.',
        'Leaving demo floors Enabled on a live hotel.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Cannot assign room to floor',
            'Floor not created or disabled',
            'Create/enable floor first',
          ],
          [
            'Floor missing in dropdown',
            'Inactive/disabled',
            'Edit floor and enable',
          ],
          [
            'Cannot delete floor',
            'Rooms still attached',
            'Reassign or remove rooms; then delete if allowed',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Floor plans upload?',
          a: 'Use Images/File Manager for media; Floors are structural masters, not image uploads.',
        },
        {
          q: 'Do OTAs need floors?',
          a: 'OTAs sell room types, not floor numbers. Floors matter for PMS ops and HK.',
        },
        {
          q: 'Can two towers share numbers?',
          a: 'Prefer unique floor numbers or distinct names including tower codes to avoid confusion.',
        },
        {
          q: 'Is floor name required?',
          a: 'Floor Number is required; Name is recommended for clarity.',
        },
        {
          q: 'Mezzanine / terrace?',
          a: 'Create them as floors with clear names so rooms can attach correctly.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'room-types',
    title: 'Room Types',
    moduleId: 'configuration',
    summary:
      'Define sellable categories with capacity, booking mode (nightly/hourly), and merchandising.',
    keywords: [
      'room types',
      'category',
      'capacity',
      'nightly',
      'hourly',
      'deluxe',
      'suite',
    ],
    pathHint: 'Configuration → Room Types',
    related: ['rooms', 'rate-plans', 'channel-mapping', 'amenities', 'images-media'],
    overview: [
      ...paragraphs(
        'Room Types are the products you sell — Deluxe, Suite, Twin, etc. Inventory counts, rates, OTA maps, and booking forms all hang off these categories.',
        'Each type carries Code, Name, Description, Physical Capacity, Base Adults, Base Children, Max Guests, Booking Mode (Nightly or Hourly), Sort Order, and Enabled status.',
        'Freeze names before channel go-live. Renaming after OTA mapping forces remaps and confuses FO arrivals (“guest booked Deluxe King, we renamed it Premium”).',
        'Do not create too many micro-types. Every extra type needs rooms, rates, maps, and staff training.'
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets([
        'Agree category list with revenue and GM',
        'Know adult/child capacity per category',
        'Decide nightly vs hourly products',
        'Plan OTA names to match',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Configuration → Room Types.',
        },
        {
          text: 'Add Code and Name (e.g. DLX / Deluxe Double).',
        },
        {
          text: 'Enter Description for staff/guest clarity.',
        },
        {
          text: 'Set Physical Capacity, Base Adults, Base Children, and Max Guests to match real beds/policy.',
        },
        {
          text: 'Choose Booking Mode: Nightly Booking or Hourly Booking.',
        },
        {
          text: 'Set Sort Order so FO lists show categories in a sensible order. Keep Enabled on.',
        },
        {
          text: 'Save. Create physical Rooms for the type, set Rate Plans / Manage Rate, then map OTAs.',
        },
      ]),
      callout(
        'important',
        'Stable names',
        'Freeze names before channel go-live. Remap Distribution the same day if you must rename.'
      ),
    ],
    screens: [
      fieldTable([
        ['Code', 'Recommended', 'Short internal/OTA-friendly code'],
        ['Name', 'Yes', 'Sellable category name'],
        ['Description', 'No', 'Details for staff/merchandising'],
        ['Physical Capacity', 'Yes', 'Physical bed/space capacity context'],
        ['Base Adults', 'Yes', 'Standard adult occupancy'],
        ['Base Children', 'Yes', 'Standard children occupancy'],
        ['Max Guests', 'Yes', 'Hard cap for the type'],
        ['Booking Mode', 'Yes', 'Nightly Booking or Hourly Booking'],
        ['Sort Order', 'No', 'Display ordering'],
        ['Enabled', 'Yes', 'Whether type is sellable'],
      ]),
    ],
    workflow: [
      workflow('Launch new category', [
        'Create room type',
        'Create rooms',
        'Attach amenities/images',
        'Create rates',
        'Map OTA',
        'Train FO',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Availability and rates are tracked per room type. Without rooms and rates, the type cannot sell even if Enabled.',
        'Capacity fields drive occupancy validation on bookings. Setting Max Guests lower than you allow at desk causes forced overrides and disputes.',
        'Hourly vs Nightly changes how stays are sold — do not flip mode casually on a type with live reservations.',
        'Soft-deleted/disabled types should stop new sales; historical bookings retain their past type reference.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Align OTA names',
        'Use the same words guests see online (Deluxe King) to speed FO reconciliation.'
      ),
      callout(
        'warning',
        'Micro-types',
        '“Deluxe Corner High Floor” as a separate type needs its own inventory — prefer notes/preferences when possible.'
      ),
      callout(
        'note',
        'Code hygiene',
        'Keep codes short and unique (DLX, STE, TWN) for mapping sheets.'
      ),
    ],
    bestPractices: [
      bullets([
        'Don’t create too many micro-types.',
        'Align names with OTA names.',
        'Document inclusions separately on rate plans (breakfast ≠ room type).',
        'Review capacity when you change bed layouts.',
        'Disable retired types instead of deleting when history exists.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Capacity lower than you actually allow.',
        'Enabling a type with zero rooms.',
        'Renaming after OTA go-live without remapping.',
        'Mixing hourly and nightly in one poorly labelled type.',
        'Duplicate names with tiny spelling differences.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Cannot book type',
            'No rooms and/or no rates',
            'Add Rooms + Manage Rate for the type',
          ],
          [
            'OTA sells wrong product',
            'Channel Mapping stale after rename',
            'Fix Channel Mapping; re-push inventory',
          ],
          [
            'Occupancy errors on booking',
            'Max Guests / base adults mis-set',
            'Correct capacity fields to match policy',
          ],
          [
            'Type missing in forms',
            'Disabled/deleted',
            'Enable the type or choose the replacement',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Virtual suites?',
          a: 'If you sell a combo of rooms, prefer a clear ops SOP. RoomNexa types map to physical rooms — do not invent phantom types without rooms.',
        },
        {
          q: 'Nightly vs Hourly?',
          a: 'Nightly Booking is standard hotel stays; Hourly Booking is for short-stay products when your property sells them.',
        },
        {
          q: 'Do amenities live on the type?',
          a: 'Amenities are maintained under Amenities and associated for merchandising; keep lists honest.',
        },
        {
          q: 'Can I change code later?',
          a: 'Possible but disruptive for maps/exports — treat codes as stable identifiers.',
        },
        {
          q: 'Sort order?',
          a: 'Controls display sequence in lists — put best-sellers or walk-in defaults where FO expects.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'rooms',
    title: 'Rooms',
    moduleId: 'configuration',
    summary: 'Create physical room numbers linked to types and floors with status fields.',
    keywords: [
      'rooms',
      'room number',
      'physical rooms',
      'ooo',
      'clean status',
    ],
    pathHint: 'Configuration → Rooms',
    related: ['room-types', 'floors', 'housekeeping-room-status', 'inventory-blocks'],
    overview: [
      ...paragraphs(
        'Rooms are the individual units (101, 102…) assigned to guests. Calendars stay empty without them — room types alone do not create door numbers.',
        'Each room needs Room Number, Room Type, Floor, Status, Clean Status, and optional Notes. Numbers must match door plates staff say aloud.',
        'Wrong type on a room causes upgrades/downgrades by accident. Duplicate numbers across floors without distinction create chaos at check-in.',
        'Temporarily remove rooms from sale with maintenance/OOO status and/or Inventory Blocks — do not delete rooms that have history unless your SOP requires it.'
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets([
        'Floors and room types already exist',
        'Door-plate list from engineering/HK',
        'Initial clean/occupancy status known',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Configuration → Rooms.',
        },
        {
          text: 'Add Room Number exactly as on the door plate.',
        },
        {
          text: 'Select Room Type and Floor.',
        },
        {
          text: 'Set Status (e.g. available / OOO as offered) and Clean Status.',
        },
        {
          text: 'Add Notes if needed (connecting door, accessible shower).',
        },
        {
          text: 'Save. Verify the room appears on Dashboard/HK boards grouped by floor.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Room Number', 'Yes', 'Door number spoken by staff'],
        ['Room Type', 'Yes', 'Sellable category'],
        ['Floor', 'Yes', 'Location grouping'],
        ['Status', 'Yes', 'Availability / OOO style operational status'],
        ['Clean Status', 'Yes', 'HK cleanliness state'],
        ['Notes', 'No', 'Ops remarks for FO/HK'],
      ]),
      bullets(
        [
          'Rooms list grouped by floor',
          'Type and status chips on room cards/rows',
        ],
        'What you see'
      ),
    ],
    workflow: [
      workflow('Open new floor', [
        'Ensure floor exists',
        'Add rooms',
        'HK board check',
        'Enable POS room service if used',
        'Sell / allot',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Sellable inventory derives from active rooms of each type minus bookings, OOO, and blocks. Phantom rooms inflate availability and cause overbook.',
        'Status and clean status drive assignment eligibility with housekeeping rules. Dirty rooms may be blocked from check-in until cleaned per SOP.',
        'Changing a room’s type while occupied/reserved needs FO coordination — future availability and maps may be affected.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Match door plates',
        'Never invent numbers staff do not say aloud.'
      ),
      callout(
        'warning',
        'Phantom rooms',
        'Do not create rooms “for OTA allotment” that do not exist — fix allotment strategy instead.'
      ),
      callout(
        'note',
        'Connecting rooms',
        'Put connecting info in Notes and train FO; types remain the sellable categories.'
      ),
    ],
    bestPractices: [
      bullets([
        'Block phantom rooms — only real doors.',
        'Mark true OOO rooms promptly.',
        'Keep numbering schemes consistent per floor (101–120).',
        'Audit room type assignments after renovations.',
        'Use notes for accessibility and connecting doors.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Wrong type on a room.',
        'Duplicate 101 on two floors without distinction.',
        'Creating rooms before floors/types exist.',
        'Leaving renovation rooms Available online.',
        'Deleting rooms instead of OOO for short works.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Not on calendar',
            'Inactive/wrong hotel/missing room',
            'Confirm room saved, active status, correct hotel',
          ],
          [
            'Cannot select floor/type',
            'Masters missing',
            'Create Floors and Room Types first',
          ],
          [
            'Still selling when OOO',
            'Status not OOO or channels not blocked',
            'Set OOO; add Inventory Block for dates/channels',
          ],
          [
            'HK cannot find room',
            'Wrong floor assignment',
            'Edit Floor on the room; refresh HK board',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Temporarily remove from sale?',
          a: 'Use maintenance/OOO and/or Inventory Blocks. Prefer that over deleting rooms with history.',
        },
        {
          q: 'Do room numbers go to OTAs?',
          a: 'OTAs typically sell types; room numbers are assigned in PMS at allocation/check-in.',
        },
        {
          q: 'Clean Status vs Status?',
          a: 'Status is broader availability/OOO style state; Clean Status is housekeeping cleanliness — both matter for assignment.',
        },
        {
          q: 'Can I bulk import?',
          a: 'Follow whatever import tools your deployment provides; otherwise enter carefully floor by floor.',
        },
        {
          q: 'POS room service?',
          a: 'Enabling rooms for POS room service is handled in POS Rooms configuration after rooms exist here.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'rate-plans',
    title: 'Rate Plans',
    moduleId: 'configuration',
    summary:
      'Create rate plans and meal-plan products (RO, EP, CP, MAP, AP) used on bookings and channels.',
    keywords: [
      'rate plan',
      'meal plan',
      'ep',
      'cp',
      'map',
      'ap',
      'room only',
      'refundable',
    ],
    pathHint: 'Configuration → Rate Plan',
    related: ['manage-rate', 'reservation', 'channel-mapping', 'finance-setup'],
    overview: [
      ...paragraphs(
        'Rate Plans define how you sell the stay as a product — Room Only, Continental Plan (breakfast), MAP, AP, and similar — including refundable vs non-refundable behaviour and cancellation policy text on the plan.',
        'Daily amounts live in Manage Rate. Creating a plan without rates means FO sees the plan missing or at zero for dates.',
        'Keep live plans limited to what FO understands. Every extra plan needs training, Manage Rate maintenance, and often channel rate codes.',
        'Corporate plans should be dedicated and controlled — enforce contracted amounts in Manage Rate and restrict who can select them.'
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets([
        'Agree inclusions with F&B (breakfast timing, meals)',
        'Decide refundable vs non-refundable products',
        'Know which plans will map to OTAs',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Configuration → Rate Plan.',
        },
        {
          text: 'Create a plan with Code and Name (e.g. CP / CP – Breakfast).',
        },
        {
          text: 'Select Meal Plan: Room Only, European Plan, Continental Plan, Modified American Plan, or American Plan as applicable.',
        },
        {
          text: 'Add Description and Cancellation Policy text FO can quote.',
        },
        {
          text: 'Set Sort Order, Refundable / Non-Refundable flag, and save.',
        },
        {
          text: 'Open Manage Rate and fill amounts for room types × dates for this plan.',
        },
        {
          text: 'If distributing, map Channel Rate Codes under Distribution → Channel Mapping.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Code', 'Yes', 'Short plan code'],
        ['Name', 'Yes', 'Guest/staff facing plan name'],
        ['Meal Plan', 'Recommended', 'RO / EP / CP / MAP / AP'],
        ['Description', 'No', 'Inclusions narrative'],
        ['Cancellation Policy', 'Recommended', 'Plan-level cancel text'],
        ['Sort Order', 'No', 'Display order'],
        ['Refundable flag', 'Yes', 'Refundable vs Non-Refundable'],
      ]),
    ],
    workflow: [
      workflow('Launch CP plan', [
        'Create Rate Plan',
        'Document breakfast inclusions',
        'Set Manage Rate',
        'Train FO',
        'Map to channels if needed',
        'Test booking + invoice',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Bookings store the chosen plan. Changing plan definitions later does not silently rewrite historical folios.',
        'Meal plan selection is product identity — breakfast cost should be reflected in Manage Rate or separate charges per your SOP, not assumed magically.',
        'Non-refundable flags inform policy behaviour; financial enforcement still depends on how cancellations and charges are posted.',
        'Unmapped plans will not sell on OTAs even if they work for walk-ins.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Naming',
        'Use guest-familiar names: “CP – Breakfast” not internal codes only.'
      ),
      callout(
        'note',
        'NRF clarity',
        'If Non-Refundable, ensure Terms & Policy and OTA cancel rules match.'
      ),
      callout(
        'warning',
        'Zero rates',
        'A plan without Manage Rate amounts is not ready for sale.'
      ),
    ],
    bestPractices: [
      bullets([
        'Limit live plans to what FO understands.',
        'Document inclusions in Description + F&B SOP.',
        'Keep corporate plans separate with contracted Manage Rate.',
        'Align cancellation text with Terms & Policy and OTAs.',
        'Review plans seasonally; disable unused ones.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Creating plans without rates.',
        'Too many overlapping BAR variants.',
        'Mapping wrong meal plan to OTA rate code.',
        'Calling a plan CP when breakfast is not actually included.',
        'Editing cancel text without briefing FO.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Plan not in booking form',
            'Inactive plan or missing rates',
            'Enable plan; set Manage Rate for dates',
          ],
          [
            'Rate shows zero',
            'Manage Rate empty',
            'Enter amounts for type/plan/date range',
          ],
          [
            'OTA rate closed',
            'No channel rate map',
            'Map Channel Rate Code; push rates',
          ],
          [
            'Guest dispute on breakfast',
            'Inclusions unclear',
            'Fix Description; train FO; adjust charges if needed',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Corporate plans?',
          a: 'Create a dedicated plan and control who can select it; enforce contracted rates in Manage Rate.',
        },
        {
          q: 'RO vs EP?',
          a: 'Room Only and European Plan are meal-plan options in the product — pick the label your property uses consistently.',
        },
        {
          q: 'Does Rate Plan set the price?',
          a: 'It defines the product. Daily prices are maintained in Manage Rate (and optionally published via Dynamic Pricing).',
        },
        {
          q: 'Refundable flag enough legally?',
          a: 'No — keep Terms & Policy and OTA rules aligned; seek legal review for your jurisdiction.',
        },
        {
          q: 'How many plans should we run?',
          a: 'As few as practical: e.g. BAR RO, BAR CP, NRF RO, plus controlled corporate — more only if ops can maintain them.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'amenities',
    title: 'Amenities',
    moduleId: 'configuration',
    summary:
      'Maintain hotel/room amenity masters by category for merchandising and ops clarity.',
    keywords: ['amenities', 'facilities', 'wifi', 'pool', 'ac', 'category'],
    pathHint: 'Configuration → Amenities',
    related: ['room-types', 'nexastay-setup', 'images-media'],
    overview: [
      ...paragraphs(
        'Amenities list what the property offers — Wi‑Fi, AC, pool, parking — organised by category cards for selection and merchandising.',
        'Honesty matters: listing under-renovation facilities causes check-in disputes and bad reviews. Only list amenities that actually work.',
        'Amenities are descriptive. They do not automatically change rates — paid services belong in extras/POS.',
        'After adding a new facility, update NexaStay/website copy and train FO so phone quotes match the system.'
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets([
        'Walk the property with HK/engineering',
        'Know which amenities are seasonal (pool/heater)',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Configuration → Amenities.',
        },
        {
          text: 'Open a category card and select amenities that apply (or add names as the UI allows).',
        },
        {
          text: 'Save selections for the category. Repeat across categories.',
        },
        {
          text: 'Attach/associate amenities to room types where your process requires type-level merchandising.',
        },
        {
          text: 'Verify public surfaces (NexaStay) and FO cheat-sheets match.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Category cards', '—', 'Groups of amenities (facilities sets)'],
        ['Amenity selection', 'Yes for listed items', 'What the property actually offers'],
        ['Save per category', '—', 'Persist selections for that category'],
      ]),
    ],
    workflow: [
      workflow('Add new facility', [
        'Confirm facility live',
        'Add/select amenity',
        'Update NexaStay/website',
        'Train FO',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Amenities describe features; they do not post charges. Sell paid amenities via extras/POS items.',
        'Category-based selection keeps lists maintainable. Over-selecting every amenity reduces trust when guests notice gaps.',
        'Seasonal amenities should be reviewed when seasons change (pool closed in monsoon, heaters in winter).'
      ),
    ],
    tips: [
      callout(
        'note',
        'Honesty',
        'Only list amenities that actually work today.'
      ),
      callout(
        'tip',
        'Seasonal review',
        'Schedule a quarterly amenity audit with HK.'
      ),
      callout(
        'warning',
        'Paid vs free',
        'If parking is paid, do not imply free parking in amenities — sell via extras.'
      ),
    ],
    bestPractices: [
      bullets([
        'Review seasonally (heater/pool).',
        'Align amenity list with OTA facility ticks.',
        'Update immediately after renovations.',
        'Keep FO one-pagers in sync.',
        'Prefer fewer true amenities over long fantasy lists.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Listing under-renovation facilities.',
        'Assuming amenities change rates.',
        'Forgetting to remove closed spa/pool.',
        'Mismatch with OTA facility checklist.',
        'Never auditing after opening month.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Not showing publicly',
            'NexaStay/type linkage or save missed',
            'Re-save category; check NexaStay/room type association',
          ],
          [
            'Guest dispute',
            'Amenity listed but unavailable',
            'Remove amenity; apologise; offer alternate',
          ],
          [
            'Cannot save selections',
            'Permission or validation',
            'Confirm manage_configuration; retry',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Paid amenities?',
          a: 'Sell via extras/POS; amenity master is not a POS item.',
        },
        {
          q: 'Per room type amenities?',
          a: 'Use type associations where available so suites can show different facilities than standards.',
        },
        {
          q: 'Do OTAs read this automatically?',
          a: 'Keep OTA extranet facilities updated to match — do not assume one-way magic for every channel.',
        },
        {
          q: 'Icons?',
          a: 'Category/amenity UI may show icons for recognition; content accuracy matters more than icons.',
        },
        {
          q: 'How often to review?',
          a: 'At least seasonally and after any renovation.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'images-media',
    title: 'Images & File Manager',
    moduleId: 'configuration',
    summary: 'Upload and organise property and room media for merchandising and direct booking.',
    keywords: ['images', 'photos', 'file manager', 'media', 'gallery'],
    pathHint: 'Configuration → Images',
    related: ['room-types', 'nexastay-setup', 'amenities'],
    overview: [
      ...paragraphs(
        'Images / File Manager stores property media used in merchandising and direct booking presentation. Photos sell the stay before the guest arrives.',
        'Show real rooms. Bait photos drive check-in disputes, chargebacks, and review damage.',
        'Large unoptimised files slow uploads and pages — compress sensibly while keeping clarity.',
        'Update immediately after renovations so NexaStay and sales decks do not show the old wing.'
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets([
        'Current photos of each room type and public areas',
        'Know which image is primary for each type if supported',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Configuration → Images (File Manager).',
        },
        {
          text: 'Upload clear landscape photos of rooms, lobby, and facilities.',
        },
        {
          text: 'Set primary images where the UI supports primary flags.',
        },
        {
          text: 'Link images to room types as needed for merchandising.',
        },
        {
          text: 'Replace outdated photos; remove duplicates cluttering the library.',
        },
        {
          text: 'Preview on NexaStay mobile to confirm load time and crop.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Upload', '—', 'Add new media files'],
        ['Gallery', '—', 'Browse existing property media'],
        ['Primary flags', 'When supported', 'Hero/primary image selection'],
        ['Room type linkage', 'Recommended', 'Associate media with categories'],
      ]),
    ],
    workflow: [
      workflow('Photo refresh', [
        'Shoot rooms',
        'Compress/export',
        'Upload',
        'Set primary',
        'Replace outdated',
        'Check NexaStay',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Media is referenced by guest-facing surfaces. Deleting a primary image can leave blank heroes until replaced.',
        'Guest ID images belong on booking/guest documents — not in property marketing Images.',
        'Filenames should be sensible for ops (deluxe-king-1.jpg) to help future staff find assets.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Show real rooms',
        'Avoid bait photos that cause check-in disputes.'
      ),
      callout(
        'note',
        'Compress',
        'Large raw camera files often fail or crawl — export web-sized JPEGs.'
      ),
      callout(
        'warning',
        'After renovation',
        'Same-day photo update prevents selling the old look online.'
      ),
    ],
    bestPractices: [
      bullets([
        'Update after renovations immediately.',
        'Keep at least one accurate photo per sellable type.',
        'Review gallery quarterly for stale shots.',
        'Prefer natural light / true colour.',
        'Do not store guest ID scans in marketing folders.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Uploading huge unoptimised files.',
        'Using stock photos of rooms you do not have.',
        'Leaving renovation scaffolding in “live” galleries.',
        'No primary image set.',
        'Mixing guest KYC images into property media.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Upload fail',
            'Size/type/network',
            'Compress; check file type; retry on stable network',
          ],
          [
            'Image not on NexaStay',
            'Not linked/primary or cache',
            'Link to type; set primary; refresh preview',
          ],
          [
            'Broken image icon',
            'Deleted file still referenced',
            'Re-upload and re-link',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Guest ID images?',
          a: 'Those belong on booking/guest documents — not property marketing images.',
        },
        {
          q: 'Video support?',
          a: 'Follow what File Manager accepts in your deployment; photos are the primary merchandising asset.',
        },
        {
          q: 'Who can upload?',
          a: 'Users with manage_configuration (or equivalent media rights on your role setup).',
        },
        {
          q: 'OTA photos?',
          a: 'Update OTA extranet galleries separately to match — keep parity with PMS media.',
        },
        {
          q: 'Watermarks?',
          a: 'Optional brand choice — keep text small so mobile crops remain useful.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'terms-policy',
    title: 'Terms & Policy',
    moduleId: 'configuration',
    summary:
      'Maintain cancellation, check-in/out, payment, pets, smoking, and related policy texts.',
    keywords: [
      'terms',
      'policy',
      'cancellation',
      'house rules',
      'pets',
      'smoking',
      'privacy',
    ],
    pathHint: 'Configuration → Terms and Policy',
    related: ['check-in', 'check-out', 'nexastay-guest', 'rate-plans'],
    overview: [
      ...paragraphs(
        'Terms & Policy stores the written rules guests and staff rely on: terms & conditions, privacy, child, cancellation, refund, check-in, check-out, pet, smoking, and age restriction policies.',
        'Policies guide behaviour and disputes. Financial enforcement still depends on how charges are posted at cancellation or checkout.',
        'OTA cancellation rules must match what you publish here and on rate plans — mismatches create chargeback risk.',
        'Version important changes with dates and brief FO so verbal quotes match the written text.'
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets([
        'GM/legal review for major policy changes',
        'Align with OTA cancel windows you actually operate',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Configuration → Terms and Policy.',
        },
        {
          text: 'Select the policy tab/type to edit (cancellation, check-in, pets, etc.).',
        },
        {
          text: 'Edit policy content in clear guest language. Keep Active when it should apply.',
        },
        {
          text: 'Note version / acknowledgment requirements if shown for that policy.',
        },
        {
          text: 'Save. Update OTA policy fields and NexaStay terms to match.',
        },
        {
          text: 'Train FO to quote the same rules; print a desk summary if helpful.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Policy type tabs', '—', 'T&Cs, privacy, child, cancel, refund, CI/CO, pet, smoking, age'],
        ['Policy content', 'Yes', 'Written rules text'],
        ['Active', 'Yes', 'Whether policy is in force'],
        ['Version', 'Display', 'Version marker when present'],
        ['Requires Acknowledgment', 'When used', 'Guest acknowledgment behaviour if enabled'],
      ]),
    ],
    workflow: [
      workflow('Update cancel policy', [
        'Legal/manager approve',
        'Edit cancellation + refund texts',
        'Align rate plan cancel text',
        'Update OTA policies',
        'Brief FO',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Policy text guides ops and guest expectations. It does not automatically post cancel fees — FO/finance must apply charges per SOP.',
        'Active policies are the ones you stand behind publicly. Keep Inactive drafts out of guest view.',
        'Child, pet, smoking, and age policies reduce lobby arguments when FO can point to written rules.'
      ),
    ],
    tips: [
      callout(
        'important',
        'Align OTAs',
        'OTA cancellation rules must match what you publish here.'
      ),
      callout(
        'tip',
        'Simple language',
        'Write short sentences guests understand under stress at the desk.'
      ),
      callout(
        'note',
        'Version major changes',
        'Put an effective date in the text when cancel windows change.'
      ),
    ],
    bestPractices: [
      bullets([
        'Keep language simple.',
        'Version major changes with date.',
        'Align rate plan cancel text with global policy.',
        'Rehearse FO exceptions process (who can override).',
        'Review annually with GM.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'FO promising exceptions that contradict written policy without approval.',
        'OTA cancel window tighter/looser than PMS text.',
        'Outdated pet policy after becoming pet-friendly.',
        'Privacy policy never updated after new messaging channels.',
        'Publishing legal jargon nobody can explain.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Guest disputes',
            'Verbal vs written mismatch',
            'Show written policy + booking terms; escalate per SOP',
          ],
          [
            'NexaStay shows old terms',
            'Not saved/synced',
            'Re-save policy; refresh public terms page',
          ],
          [
            'FO unsure which rule applies',
            'Multiple conflicting texts',
            'Consolidate; brief team; disable obsolete versions',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Legally binding?',
          a: 'Treat as operational terms; seek legal review for your jurisdiction.',
        },
        {
          q: 'Which policy types exist?',
          a: 'Including terms & conditions, privacy, child, cancellation, refund, check-in, check-out, pet, smoking, and age restriction.',
        },
        {
          q: 'Does this set cancel fees automatically?',
          a: 'No — text guides behaviour; posting fees is a billing/ops action.',
        },
        {
          q: 'Can policies require acknowledgment?',
          a: 'When Requires Acknowledgment is enabled for a policy, follow the guest acknowledgment flow your property uses.',
        },
        {
          q: 'Who edits policies?',
          a: 'Managers with configuration rights after GM/legal input — not ad-hoc FO edits.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'finance-setup',
    title: 'Finance Setup (Tax / GST)',
    moduleId: 'configuration',
    summary:
      'Configure tax rules (GST/CGST/SGST), applicability, and GSTIN/PAN used in billing.',
    keywords: [
      'finance setup',
      'tax',
      'gst',
      'gstin',
      'cgst',
      'sgst',
      'pan',
    ],
    pathHint: 'Configuration → Finance Setup',
    related: ['invoice-setup', 'reports-gst', 'draft-invoice', 'rate-plans'],
    overview: [
      ...paragraphs(
        'Finance Setup defines how taxes calculate on bills and stores tax registration details (GSTIN, PAN). Wrong setup produces wrong invoices and GST reports.',
        'Tax rules include Name, Tax Type (GST, CGST, SGST…), Applicable On (Room Rate, Food & Beverage, Services, Total Amount, Other), percentage or fixed amount, optional min/max amount, currency, description, sort order, and Active flag.',
        'Example: room GST may differ from restaurant F&B — configure separate rules with correct Applicable On values rather than one blunt percentage everywhere.',
        'Get CA sign-off before go-live and before any mid-year rate change. Do not invent tax percentages.'
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets([
        'Know GST rates applicable to rooms vs F&B for your property',
        'GSTIN and PAN documents handy',
        'CA/accountant available for sign-off',
      ]),
      callout(
        'warning',
        'Get CA sign-off',
        'Do not invent tax percentages or split logic without advice.'
      ),
    ],
    steps: [
      steps([
        {
          text: 'Open Configuration → Finance Setup.',
        },
        {
          text: 'Enter GST Number (GSTIN) and PAN Number; save GST details (15-character GSTIN appears on tax invoices).',
        },
        {
          text: 'Add a tax rule: Name, Tax Type, Applicable On, Tax Percentage (%) and/or Tax Amount (Fixed).',
        },
        {
          text: 'Optional: set Minimum Amount / Maximum Amount thresholds, Currency, Description, Sort Order.',
        },
        {
          text: 'Set Active on. Save the rule. Repeat for CGST/SGST splits or F&B rules as required.',
        },
        {
          text: 'Create a test booking invoice and a POS bill if applicable; verify tax lines with your CA checklist.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['GST Number (GSTIN)', 'Yes for GST invoices', '15-character registration on tax invoices'],
        ['PAN Number', 'Recommended', 'PAN linked to tax identity'],
        ['Rule Name', 'Yes', 'Label for the tax rule'],
        ['Tax Type', 'Yes', 'GST / CGST / SGST (as offered)'],
        ['Applicable On', 'Yes', 'Room Rate / Food & Beverage / Services / Total / Other'],
        ['Tax Percentage (%)', 'One of % or fixed', 'Percentage calculation'],
        ['Tax Amount (Fixed)', 'One of % or fixed', 'Fixed tax amount alternative'],
        ['Min / Max Amount', 'No', 'Threshold band for applicability'],
        ['Currency', 'Recommended', 'Rule currency context'],
        ['Active', 'Yes', 'Whether rule applies at billing'],
      ]),
    ],
    workflow: [
      workflow('Tax change', [
        'CA advice',
        'Update Finance Setup rules/GSTIN',
        'Test room invoice + F&B bill',
        'Inform FO and accounts',
        'Monitor GST reports',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'The tax engine applies Active rules at billing time based on Applicable On. Inactive rules do not calculate.',
        'GSTIN/PAN identify the hotel on tax invoices. Wrong GSTIN is a compliance issue — correct and reissue carefully per accounts SOP.',
        'Percentage vs fixed amount: use the field that matches how the tax is defined; do not double-apply accidentally with overlapping rules.',
        'Room vs restaurant tax differences are handled by Applicable On (Room Rate vs Food & Beverage), not by hoping one rule covers both correctly.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'Test both room and F&B',
        'Always test a room folio and a restaurant bill after tax edits.'
      ),
      callout(
        'warning',
        'Mid-month changes',
        'Changing percentages mid-month without CA process corrupts filing reconciliations.'
      ),
      callout(
        'note',
        'Sort order',
        'Use Sort Order when multiple lines must print in a stable sequence on invoices.'
      ),
    ],
    bestPractices: [
      bullets([
        'Lock edits after go-live except regulated changes.',
        'Keep CA email approval with each change.',
        'Name rules clearly (“Room GST 12%”, “F&B CGST 2.5%”).',
        'Reconcile sample invoices to GST reports monthly.',
        'Align Invoice Setup HSN/SAC with how you tax rooms vs food.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Forgetting room vs restaurant tax differences.',
        'Inactive rules left off after a “temporary” test.',
        'Typos in GSTIN.',
        'Overlapping rules double-charging.',
        'Changing tax without testing POS bills.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Tax zero on bill',
            'Rules missing/inactive or wrong Applicable On',
            'Activate correct rules for Room Rate / F&B',
          ],
          [
            'Wrong GSTIN on invoice',
            'GSTIN field outdated',
            'Edit Finance Setup; reissue per accounts SOP',
          ],
          [
            'Tax too high',
            'Duplicate rules or wrong %',
            'Disable duplicates; correct percentage with CA',
          ],
          [
            'F&B untaxed',
            'No Food & Beverage applicable rule',
            'Add Active F&B rule',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Multi-tax?',
          a: 'Configure each rule your property must charge (e.g. CGST + SGST) per Finance Setup capabilities.',
        },
        {
          q: 'Does Invoice Setup replace this?',
          a: 'No — Invoice Setup brands documents; Finance Setup calculates tax and stores GSTIN/PAN.',
        },
        {
          q: 'Fixed amount vs percentage?',
          a: 'Use percentage for typical GST; fixed amount only when your tax definition requires it.',
        },
        {
          q: 'Can FO edit tax rules?',
          a: 'Should not — restrict to configuration/finance roles.',
        },
        {
          q: 'What about exempt guests?',
          a: 'Follow your fiscal SOP and role permissions for exemptions; do not casually disable global rules.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'invoice-setup',
    title: 'Invoice Setup',
    moduleId: 'configuration',
    summary:
      'Brand invoices with templates, logo fields, HSN/SAC, bank details, and footer texts.',
    keywords: [
      'invoice setup',
      'logo',
      'billing branding',
      'hsn',
      'sac',
      'ifsc',
      'template',
    ],
    pathHint: 'Configuration → Invoice Setup',
    related: ['finance-setup', 'draft-invoice', 'check-out', 'basic-details'],
    overview: [
      ...paragraphs(
        'Invoice Setup controls how invoices look and what supporting text they carry — templates, header tagline, authorized signatory, service description, HSN/SAC codes, footer notes, bank details, and terms.',
        'You can maintain templates with Active and Default flags, clone templates, and customise names. Always generate a sample PDF after changes.',
        'HSN/SAC fields distinguish room stay vs restaurant food lines for compliance presentation. Keep them aligned with how Finance Setup taxes those lines.',
        'Branding must match Basic Details identity. A new logo with an old legal name (or vice versa) looks unprofessional and confuses accounts payable on the guest side.'
      ),
    ],
    beforeYouBegin: [
      cfgPerm,
      bullets([
        'High-contrast logo file that prints in B/W',
        'Bank name, account holder, account number, IFSC',
        'HSN/SAC codes from your CA for room and F&B',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Configuration → Invoice Setup.',
        },
        {
          text: 'Select or create a template; set Active and Default as needed. Clone if you want a safe copy before edits.',
        },
        {
          text: 'Set Header tagline, Authorized signatory, Service description.',
        },
        {
          text: 'Enter Hotel HSN/SAC (room stay) and Restaurant HSN/SAC (food).',
        },
        {
          text: 'Fill Footer note, Bottom bar message, and Terms & conditions text for the invoice.',
        },
        {
          text: 'Enter Bank name, Account holder, Account number, IFSC code if you show bank details on invoices.',
        },
        {
          text: 'Save. Generate a sample invoice PDF from a test booking and a POS bill if used.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['Template Active / Default', 'Yes', 'Which template is live/default'],
        ['Custom template name', 'When custom', 'Label for cloned/custom templates'],
        ['Header tagline', 'No', 'Brand line under header'],
        ['Authorized signatory', 'No', 'Signatory name on invoice'],
        ['Service description', 'No', 'Default service narrative'],
        ['Hotel HSN/SAC', 'Recommended', 'Room stay classification code'],
        ['Restaurant HSN/SAC', 'Recommended', 'Food classification code'],
        ['Footer / bottom bar / terms', 'No', 'Footer messages and invoice T&Cs'],
        ['Bank name / holder / account / IFSC', 'Optional', 'Payment details on invoice'],
        ['Clone / Delete template', '—', 'Template lifecycle actions'],
      ]),
    ],
    workflow: [
      workflow('Rebrand invoices', [
        'Prepare logo + bank details',
        'Update Invoice Setup',
        'Test PDF room + F&B',
        'Inform accounts',
        'Retire old default template',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Setup applies to generated invoice documents going forward. Historical PDFs already issued are not retroactively redesigned.',
        'Default template is used when multiple templates exist — ensure only one intended default.',
        'Tax amounts still come from Finance Setup rules; Invoice Setup presents identity, codes, and bank text.'
      ),
    ],
    tips: [
      callout(
        'tip',
        'High-contrast logo',
        'Use a logo that prints clearly in black-and-white too.'
      ),
      callout(
        'note',
        'Clone before edit',
        'Clone the live template before experimental layout changes.'
      ),
      callout(
        'warning',
        'Transparent logos',
        'Transparent logos can vanish on light or dark PDF backgrounds — test both.'
      ),
    ],
    bestPractices: [
      bullets([
        'Include support phone on footer if available.',
        'Keep HSN/SAC aligned with CA advice.',
        'Test room and restaurant invoice samples.',
        'Mark one Default template only.',
        'Store the approved PDF sample with accounts.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Transparent logo invisible on PDF.',
        'Wrong IFSC/account number.',
        'Updating logo but not legal header/signatory.',
        'Leaving an old template as Default.',
        'HSN/SAC blank when accounts expected codes.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'Logo missing',
            'Upload/cache/template',
            'Re-upload; clear cache; regenerate; confirm Default template',
          ],
          [
            'Old bank details',
            'Fields not saved / wrong template',
            'Edit Default template bank fields; save; retest PDF',
          ],
          [
            'Tax wrong but layout fine',
            'Finance Setup issue',
            'Fix tax rules — Invoice Setup will not recalculate tax logic',
          ],
          [
            'Restaurant code missing on F&B bill',
            'Restaurant HSN/SAC empty',
            'Fill Restaurant HSN/SAC; retest',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Separate restaurant invoice brand?',
          a: 'Follow your fiscal SOP; POS e-bills may use restaurant contexts while room folios use hotel templates.',
        },
        {
          q: 'Does this set GSTIN?',
          a: 'GSTIN is maintained in Finance Setup; Invoice Setup presents branding and codes.',
        },
        {
          q: 'Can I keep multiple templates?',
          a: 'Yes — use Active/Default carefully and clone for experiments.',
        },
        {
          q: 'Are bank details required?',
          a: 'Optional, but useful when guests pay by transfer — keep them accurate.',
        },
        {
          q: 'Who approves template changes?',
          a: 'Accounts/GM typically — FO should not freestyle invoice branding.',
        },
      ]),
    ],
  }),

  buildGuide({
    id: 'hotel-users',
    title: 'Hotel Users',
    moduleId: 'configuration',
    summary: 'Manage staff users for the selected hotel property with least-privilege roles.',
    keywords: ['users', 'staff accounts', 'hotel users', 'roles', 'offboarding'],
    pathHint: 'Users (hotel) / Configuration-related user management',
    related: ['users-roles', 'first-login', 'configuration-overview'],
    overview: [
      ...paragraphs(
        'Hotel Users covers staff who operate this property: creating logins, assigning roles, and disabling access when people leave.',
        'Permissions come from roles (RBAC). A receptionist does not need Configuration or Dynamic Pricing; a cashier does not need Distribution mapping.',
        'Organisation-level administration also exists on Landing → Settings → Users. Use the surface your role provides; always confirm the staff member is assigned to the correct hotel.',
        'Offboarding is a security control: disable users on the last working day. Shared passwords destroy audit trails and PCI-minded payment discipline.'
      ),
    ],
    beforeYouBegin: [
      permissionTable([['manage_users', 'Create/manage users']]),
      cfgPerm,
      bullets([
        'Know the role matrix (FO, cashier, HK, revenue, GM)',
        'Have work email/mobile for the staff member',
        'Agree who approves privileged roles',
      ]),
    ],
    steps: [
      steps([
        {
          text: 'Open Users management for the hotel (Users route / hotel user admin as permitted).',
        },
        {
          text: 'Add user with name, email/login, and secure temporary password or invite flow.',
        },
        {
          text: 'Assign the least-privilege role that still lets them do their job.',
        },
        {
          text: 'Confirm hotel assignment so they cannot post to the wrong property.',
        },
        {
          text: 'Share credentials securely (not public chat). Walk First Login on a non-production task.',
        },
        {
          text: 'On exit: disable/deactivate the user the same day; revoke shared device sessions.',
        },
      ]),
    ],
    screens: [
      fieldTable([
        ['User list', '—', 'Existing staff accounts and status'],
        ['Name / email / username', 'Yes', 'Identity and login'],
        ['Role', 'Yes', 'Permission bundle'],
        ['Status Active/Inactive', 'Yes', 'Access control'],
        ['Invite/create / edit', '—', 'Lifecycle actions'],
      ]),
    ],
    workflow: [
      workflow('Onboard receptionist', [
        'Create user',
        'Assign FO role',
        'First login guide',
        'Shadow shift',
        'Remove temp privileges if any',
      ]),
    ],
    businessLogic: [
      ...paragraphs(
        'Roles gate menus and actions. Missing modules are usually permissions or package — not random bugs.',
        'Inactive users cannot login. Prefer deactivate over delete when you need audit history.',
        'Org-level users administration can span multiple hotels; hotel assignment still matters for operational posting.'
      ),
    ],
    tips: [
      callout(
        'important',
        'Offboarding',
        'Disable users immediately when staff leave — same day, not next week.'
      ),
      callout(
        'tip',
        'No shared logins',
        'Every cashier needs their own user so voids and refunds are attributable.'
      ),
      callout(
        'warning',
        'Privilege creep',
        'Do not hand Configuration or Distribution to everyone “for convenience”.'
      ),
    ],
    bestPractices: [
      bullets([
        'No shared passwords.',
        'Separate cashier vs manager roles.',
        'Review user list monthly for leavers.',
        'Use least privilege; add rights only when proven necessary.',
        'Keep a joiners/leavers checklist with IT/GM.',
      ]),
    ],
    commonMistakes: [
      bullets([
        'Giving everyone configuration rights.',
        'Leaving ex-staff Active.',
        'Shared “front desk” login.',
        'Wrong hotel assignment on multi-property users.',
        'Sending passwords in public WhatsApp groups.',
      ]),
    ],
    troubleshooting: [
      table(
        ['Problem', 'Likely cause', 'What to do'],
        [
          [
            'User cannot see module',
            'Role lacks permission or package',
            'Adjust role permissions; confirm package modules',
          ],
          [
            'Cannot login',
            'Inactive flag or bad password',
            'Activate user; reset password; verify hotel assignment',
          ],
          [
            'Posts to wrong hotel',
            'Multi-hotel assignment confusion',
            'Train top-bar hotel selection; restrict assignments if needed',
          ],
          [
            'Cannot create user',
            'Missing manage_users',
            'Ask org admin for user-management permission',
          ],
        ]
      ),
    ],
    faqs: [
      faq([
        {
          q: 'Org-level users?',
          a: 'Also managed from Landing → Settings → Users for organisation administration.',
        },
        {
          q: 'Where are roles defined?',
          a: 'See Users & Roles guides — roles bundle feature permissions like manage_booking_operations or manage_configuration.',
        },
        {
          q: 'Can one user work two hotels?',
          a: 'If assigned to both, they must select the correct hotel before posting. Train this explicitly.',
        },
        {
          q: 'Should FO have manage_distribution?',
          a: 'Usually no — escalate mapping/allotment to revenue.',
        },
        {
          q: 'What about agency staff?',
          a: 'Create time-bound users with minimal roles; disable when the contract ends.',
        },
      ]),
    ],
  }),
];
